import { useCallback, useState, useId, type FC } from "react";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { SortableWorkflowEditStepItem, WorkflowEditStepItem } from "@/components/automations/AutomationEditStepItem";
import type {
    WorkflowEditFormStepData,
    WorkflowEditFormLimitsData,
} from "@/components/automations/automation-edit-form-data";
import { v4 as uuidv4 } from "uuid";
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    KeyboardSensor,
    type DragEndEvent,
    type DragStartEvent,
    type UniqueIdentifier,
} from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { Card } from "@/components/flexkit/Card";

type WorkflowEditActionProps = {
    steps: WorkflowEditFormStepData[];
    onStepsChange: (steps: WorkflowEditFormStepData[]) => void;
    limits?: WorkflowEditFormLimitsData;
    onLimitsChange: (limits: WorkflowEditFormLimitsData | undefined) => void;
};

export const WorkflowEditAction: FC<WorkflowEditActionProps> = ({ steps, onStepsChange, limits, onLimitsChange }) => {
    const maxParallelId = useId();
    const maxTotalId = useId();
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = steps.findIndex((step) => step.id === active.id);
        const newIndex = steps.findIndex((step) => step.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
            onStepsChange(arrayMove(steps, oldIndex, newIndex));
        }
    };

    const addStep = useCallback(() => {
        const newStep = {
            id: `step-${uuidv4()}`,
            type: "prompt" as const,
            content: "",
        } satisfies WorkflowEditFormStepData;
        onStepsChange([...steps, newStep]);
    }, [steps, onStepsChange]);

    const updateStep = useCallback(
        (stepId: string, updated: WorkflowEditFormStepData) => {
            const updatedSteps = steps.map((step) => (step.id === stepId ? updated : step));
            onStepsChange(updatedSteps);
        },
        [steps, onStepsChange],
    );

    const removeStep = useCallback(
        (stepId: string) => {
            const updatedSteps = steps.filter((step) => step.id !== stepId);
            onStepsChange(updatedSteps);
        },
        [steps, onStepsChange],
    );

    const activeStep = activeId ? steps.find((step) => step.id === activeId) : undefined;

    const handleMaxParallelChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numValue = value === "" ? undefined : parseInt(value, 10);
            onLimitsChange({
                ...limits,
                maxParallel: numValue,
            });
        },
        [limits, onLimitsChange],
    );

    const handleMaxTotalChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            const numValue = value === "" ? undefined : parseInt(value, 10);
            onLimitsChange({
                ...limits,
                maxTotal: numValue,
            });
        },
        [limits, onLimitsChange],
    );

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-4">
                    <InputField
                        label="Max concurrent actions"
                        hint="The number of actions that can run in parallel per execution"
                        id={maxParallelId}
                    >
                        <Input
                            id={maxParallelId}
                            type="number"
                            min={1}
                            max={25}
                            value={limits?.maxParallel ?? ""}
                            onChange={handleMaxParallelChange}
                            placeholder="No limit"
                            className="max-w-[300px]"
                            data-testid="max-parallel-input"
                        />
                    </InputField>
                    <InputField
                        label="Max total actions"
                        hint="The total number of actions that can be created by a single execution"
                        id={maxTotalId}
                    >
                        <Input
                            id={maxTotalId}
                            type="number"
                            min={1}
                            max={100}
                            value={limits?.maxTotal ?? ""}
                            onChange={handleMaxTotalChange}
                            placeholder="No limit"
                            className="max-w-[300px]"
                            data-testid="max-total-input"
                        />
                    </InputField>
                </div>
                <div className="text-sm text-content-secondary">
                    Note: These limits are currently restricted during beta to ensure system stability. Higher limits
                    will be available after the beta period.
                </div>
            </div>
            <Card variant="bordered" className="flex flex-col gap-4 border-border-subtle bg-surface-primary">
                <div className="flex items-center justify-between">
                    <Heading2 className="text-md font-medium -tracking-[1px] text-content-primary">Steps</Heading2>
                    <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={addStep}
                        data-testid="add-step-button"
                        LeadingIcon={IconPlus}
                        data-tracking-id="add-step-workflow-edit-action"
                    >
                        New Step
                    </Button>
                </div>
                {steps.length === 0 ? (
                    <div className="text-sm text-content-destructive">At least one step is required.</div>
                ) : (
                    <DndContext
                        sensors={sensors}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                    >
                        <SortableContext items={steps.map((step) => step.id)} strategy={verticalListSortingStrategy}>
                            <div className="flex flex-col">
                                {steps.map((step) => (
                                    <SortableWorkflowEditStepItem
                                        key={step.id}
                                        step={step}
                                        onUpdate={(updates) => updateStep(step.id, updates)}
                                        onRemove={() => removeStep(step.id)}
                                    />
                                ))}
                            </div>
                        </SortableContext>

                        <DragOverlay dropAnimation={{ duration: 100 }}>
                            {activeStep && (
                                <WorkflowEditStepItem
                                    isDragOverlay={true}
                                    step={activeStep}
                                    onUpdate={(updates) => updateStep(activeStep.id, updates)}
                                    onRemove={() => removeStep(activeStep.id)}
                                />
                            )}
                        </DragOverlay>
                    </DndContext>
                )}
            </Card>
        </div>
    );
};
